import React, { useMemo, useState } from 'react';
import { StyleSheet, View, Text } from 'react-native';
import GameDropDownButtonOblong from './Button/GameDropDownButtonOblong';
import { GAME_TYPES_TYPE } from '../../constants/constants';
import GameDropDownScroll from './GameDropDownScroll';
import { useSignedInContext } from '../../context/SignedInContext';
import { usePlayFriendGamesQuery } from '../screens/Gym/Stats/usePlayFriendGamesQuery.gql';

type RowData = {
  label: string;
  played: number;
  won: number;
  winPercent: number;
};

type StatsTablePlayFriendProps = {
  queryUserId?: string;
};

export default function StatsTablePlayFriend(props: StatsTablePlayFriendProps) {
  const { queryUserId } = props;
  const { user } = useSignedInContext();

  const userId = queryUserId || user?.id || '';
  const [gameSelected, setGameSelected] = useState<GAME_TYPES_TYPE | 'All'>('All');
  const [showDropdown, setShowDropdown] = useState(false);

  const {
    data: AIdata,
    loading,
    error,
  } = usePlayFriendGamesQuery(
    {
      userId,
      gameType: gameSelected === 'All' ? undefined : gameSelected,
    },
    { skip: !userId },
  );

  // 🔥 Aggregate data by opponentName + overall stats
  const { opponentStats, overallStats } = useMemo(() => {
    const rows = AIdata?.playFriendGamesByUserHiveAndGameType || [];

    const opponentStats: RowData[] = rows.map(row => ({
      label: row.opponentName,
      played: row.played,
      won: row.won,
      winPercent: Math.round((row.won / row.played) * 100) || 0,
    }));

    const totalPlayed = opponentStats.reduce((sum, r) => sum + r.played, 0);
    const totalWon = opponentStats.reduce((sum, r) => sum + r.won, 0);

    const overallStats: RowData = {
      label: 'Overall',
      played: totalPlayed,
      won: totalWon,
      winPercent: totalPlayed ? Math.round((totalWon / totalPlayed) * 100) : 0,
    };

    return { opponentStats, overallStats };
  }, [AIdata]);

  return (
    <View style={[styles.tableContainer, !loading && { minHeight: 238 }]}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerViewButton}>
          <GameDropDownButtonOblong
            gameSelected={gameSelected}
            setGameSelected={setGameSelected}
            setShowDropdown={() => setShowDropdown(!showDropdown)}
          />
        </View>
        {['Played', 'Won', 'Win%'].map(column => (
          <View style={styles.headerViewStats} key={column}>
            <Text style={styles.headerText}>{column}</Text>
          </View>
        ))}
      </View>

      {/* Body */}
      {loading && (
        <View style={styles.row}>
          <Text style={styles.bodyText}>Loading...</Text>
        </View>
      )}

      {error && (
        <View style={styles.row}>
          <Text style={styles.bodyText}>Error loading data</Text>
        </View>
      )}

      {/* Overall row */}
      {!loading && !error && overallStats.played > 0 && (
        <>
          <View style={styles.row}>
            <View style={styles.cellLabel}>
              <Text style={[styles.bodyText, { fontWeight: '700' }]}>{overallStats.label}</Text>
            </View>
            <View style={styles.cellStat}>
              <Text style={styles.bodyText}>{overallStats.played}</Text>
            </View>
            <View style={styles.cellStat}>
              <Text style={styles.bodyText}>{overallStats.won}</Text>
            </View>
            <View style={styles.cellStat}>
              <Text style={styles.bodyText}>{overallStats.winPercent}%</Text>
            </View>
          </View>
          <View style={styles.divider} />
        </>
      )}

      {/* Opponent rows */}
      {!loading &&
        !error &&
        opponentStats.map((row, idx) => (
          <View key={row.label}>
            <View style={styles.row}>
              <View style={styles.cellLabel}>
                <Text style={styles.bodyText}>{row.label}</Text>
              </View>
              <View style={styles.cellStat}>
                <Text style={styles.bodyText}>{row.played}</Text>
              </View>
              <View style={styles.cellStat}>
                <Text style={styles.bodyText}>{row.won}</Text>
              </View>
              <View style={styles.cellStat}>
                <Text style={styles.bodyText}>{row.winPercent}%</Text>
              </View>
            </View>
            {idx < opponentStats.length - 1 && <View style={styles.divider} />}
          </View>
        ))}

      {showDropdown && (
        <GameDropDownScroll setShowDropdown={setShowDropdown} setGameSelected={setGameSelected} />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  tableContainer: {
    width: '100%',
    borderRadius: 16,
    backgroundColor: '#FFF5DD',
    padding: 10,
    borderWidth: 2,
    borderColor: '#FFCF53',
    position: 'relative',
    borderBottomWidth: 10,
    borderLeftWidth: 7,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1.5,
    borderColor: '#E5C87F',
    paddingBottom: 6,
    marginBottom: 4,
  },
  headerText: {
    fontFamily: 'Secular One',
    fontWeight: '700',
    fontSize: 15,
    color: '#333333',
  },
  headerViewButton: {
    flex: 1,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  headerViewStats: {
    width: 55,
    alignItems: 'center',
    justifyContent: 'center',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  cellLabel: {
    flex: 1,
    justifyContent: 'center',
  },
  cellStat: {
    width: 55,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bodyText: {
    fontFamily: 'Secular One',
    fontSize: 14,
    color: '#453B3B',
  },
  divider: {
    height: 1,
    backgroundColor: '#E5C87F',
    marginVertical: 2,
  },
});
